import api from "../utils/api";
import { isGuid } from "../utils/string";
import { LOGOUT } from "./types";
import { pack } from "./alert";
import { JavaScripttoXML } from "../utils/serialize";
import { applyCustomValues } from "../utils/template-custom-fields";

export const get = (uuid=null, name=null) => async (dispatch) => {
    try {
        const res = await api.get(`/reports/templates/${isGuid(uuid) ? uuid : !!name ? "n/"+name : ""}`);
        return res.data?.items;
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
}

export const flagged = ({ flags }) => async (dispatch) => {
    try {
        const res = await api.get(`/reports/templates/f/${flags}`);
        return res.data?.items ?? [];
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        return [];
    }
}

export const global = () => async (dispatch) => {
    try {
        const res = await api.get("/reports/templates/g");
        return res.data?.items ?? [];
    } catch (err) {
        console.log(err);
        //dispatch({type: LOGOUT});
    }
}

export const register = ({ parentUuid, name, caption, flags, hidden, configuration }) => async (dispatch) => {
    try {
        const res = await api.post("/reports/templates/r", {
            parentUuid,
            name,
            caption,
            flags,
            hidden,
            configuration
        });
        dispatch(pack(["report template imported"], "success"));
        return res.data ?? {};
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
}

export const save = ({
    category,
    name,
    flags,
    hidden,
    configuration,
    caption,
    fileName,
    output,
    content,
    uuid
}) => async (dispatch) => {
    try {
        const res = await api.post(`/reports/templates`, {
            category,
            name,
            flags,
            hidden,
            configuration,
            caption,
            fileName,
            output,
            content,
            uuid
        });
        dispatch(pack(["report template saved"], "success"));
        return res.data;
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
}

export const destroy = (uuid) => async (dispatch) => {
    try {
        const res = await api.delete(`/reports/templates/${uuid}`);
        dispatch(pack(["report template deleted"], "success"));
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        else dispatch(pack(["report template delete failed"], "error"));
    }
}

export const components = ({ type, active = true } = {}) => async () => {
    const params = new URLSearchParams();
    if (type) params.set("type", type);
    if (active !== undefined) params.set("active", active ? "true" : "false");
    const suffix = params.toString() ? `?${params}` : "";
    const res = await api.get(`/reports/templates/components${suffix}`);
    return res.data?.items || [];
};

export const saveComponent = (component) => async (dispatch) => {
    const hasUuid = isGuid(component?.uuid);
    const res = hasUuid
        ? await api.put(`/reports/templates/components/${component.uuid}`, component)
        : await api.post("/reports/templates/components", component);
    dispatch(pack([`report ${String(component.type || "component").toLowerCase()} saved`], "success"));
    return res.data?.item || null;
};

export const destroyComponent = (uuid) => async (dispatch) => {
    const res = await api.delete(`/reports/templates/components/${uuid}`);
    dispatch(pack(["report component deleted"], "success"));
    return res.data;
};

export const componentAssignments = (uuid) => async () => {
    if (!isGuid(uuid)) return [];
    const res = await api.get(`/reports/templates/${uuid}/assignments`);
    return res.data?.items || [];
};

export const saveComponentAssignments = (uuid, assignments) => async () => {
    if (!isGuid(uuid)) throw new Error("Save the report template before assigning headers or footers.");
    const res = await api.put(`/reports/templates/${uuid}/assignments`, { assignments });
    return res.data?.items || [];
};

export const compileTemplate = (uuid) => async (dispatch) => {
    if (!isGuid(uuid)) throw new Error("Save the report template before compiling it.");
    const res = await api.post(`/reports/templates/${uuid}/compile`, {});
    dispatch(pack(["report template compiled"], "success"));
    return res.data;
};

export const templateCompilation = (uuid) => async () => {
    if (!isGuid(uuid)) return { status: "FULL", compilation: null };
    const res = await api.get(`/reports/templates/${uuid}/compilation`);
    return res.data || { status: "FULL", compilation: null };
};

/*
export const render = ({uuid, xml, xsl, contentType, json, output }) => async (dispatch) => {
    try {
        if (json && !xml) xml = JavaScripttoXML(json); 
        const res = await api.post(`/reports/render`, { uuid, xml, xsl, contentType, output });
        return res.data;
    } catch (err) {
        // dispatch({ type: LOGOUT });
    }
}
*/

const hasText = (value) =>
  typeof value === "string" && value.trim().length > 0;

export const render =
    ({ uuid, xml, xsl, contentType, json, custom, output }) =>
        async (dispatch) => {
            try {
                if (json && custom) json = applyCustomValues(json, custom);
                const resolvedXml = hasText(xml)
                    ? xml
                    : json
                        ? JavaScripttoXML(json)
                        : null;

                const hasUuid = isGuid(uuid);
                const hasAdHocPayload = hasText(resolvedXml) && hasText(xsl);

                if (!hasUuid && !hasAdHocPayload) {
                    throw new Error(
                        "Insufficient render data. Provide either a valid uuid, or both xml and xsl."
                    );
                }

                const wantsBlob =
                    ["RTF", "PDF", "XSL", "DXP"].includes(output) ||
                    (hasUuid && !output);

                const payload = {
                    uuid: hasUuid ? uuid : undefined,
                    xml: resolvedXml,
                    xsl,
                    contentType,
                    output,
                };

                const res = await api.post(
                    "/reports/render",
                    payload,
                    wantsBlob ? { responseType: "blob" } : undefined
                );

                return res.data;
            } catch (err) {
                try {
                    if (err?.response?.data instanceof Blob) {
                        console.error(await err.response.data.text());
                    }
                } catch { }

                throw err;
            }
        };

export const prepareSignature =
    ({ uuid, xml, xsl, contentType, json, custom, output, recipients, recipient, ...context }) =>
        async (dispatch) => {
            try {
                if (json && custom) json = applyCustomValues(json, custom);
                if (json && typeof json === "object" && !Array.isArray(json)) {
                    json = {
                        ...json,
                        prepare: true,
                    };
                }
                const resolvedXml = hasText(xml)
                    ? xml
                    : json
                        ? JavaScripttoXML(json)
                        : null;

                if (!isGuid(uuid)) {
                    throw new Error("A report template is required to prepare a signature request.");
                }

                const res = await api.post("/reports/render/signature", {
                    uuid,
                    xml: resolvedXml,
                    xsl,
                    contentType,
                    output,
                    recipients,
                    recipient,
                    ...context,
                });

                return res.data;
            } catch (err) {
                throw err;
            }
        };

export const inline = ({ text, xml, json, custom }) => async (dispatch) => {
    try {
        if (json && custom) json = applyCustomValues(json, custom);
        if (json && !xml) xml = JavaScripttoXML(json); 
        var xsl =
           `<xsl:stylesheet version="1.0" xmlns:xsl="http://www.w3.org/1999/XSL/Transform" xmlns:xs="http://www.w3.org/2001/XMLSchema">`+
           `<xsl:output method="text"/>`+
            `<xsl:template match="/data">${text}</xsl:template>`+
            `</xsl:stylesheet>`;
        const res = await dispatch(render({ xml, xsl }));
        return JSON.parse(res).result;
    } catch (err) {
        console.log(err);
        // dispatch({ type: LOGOUT });
    }

}
